import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { MyLeadsCreateAudienceRequestParams } from '@mm/state/interfaces/myLeads';
import { notification } from 'antd';
import AudiencesModel, { FilterItem } from '@mm/state/model/AudiencesModel';
import { useExportContactsAction } from '../Audiences/ExportContacts/ExportContacts';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useSelector } from 'react-redux';
import { ObjectUtils } from '@mm/utils';

export const useCreateMyLeadsAudience = (onSuccess?: () => void) => {
  const { create } = useAudiencesCRUDActions();
  const { openAddAudienceToAutomationModal, closeModal } = useModalActions();

  const onExportSuccess = (name?: string) => {
    notification.success({
      message: name
        ? `Audience ${name} export has been scheduled.`
        : 'Contacts export has been scheduled.',
    });
    onSuccess?.();
  };
  const exportContacts = useExportContactsAction();
  const history = useHistory();
  const chatbotId = useSelector(activeChatbotIdSelector);

  const onAddToAutomation = (activeAudience: AudiencesModel) => {
    openAddAudienceToAutomationModal({
      activeAudience,
      closeModal,
      usePurchaseLeadsLayout: true,
    });
  };

  const createMyLeadsAudience = async ({
    params,
    withAddToAutomation,
    withExport,
    isSelectedAll,
  }: {
    params: MyLeadsCreateAudienceRequestParams;
    withAddToAutomation?: boolean;
    withExport?: boolean;
    isSelectedAll?: boolean;
  }) => {
    const { page, query, tagsFilters, filterParams, esFilters, ...restParams } =
      params;

    const hasFilters = () => ({
      filterParamsExist: !!Object.keys(filterParams || {}).length,
      tagsFiltersExist: !!Object.keys(tagsFilters || {}).length,
      esFiltersExist: !!Object.keys(esFilters || {}).length,
    });
    const { filterParamsExist, tagsFiltersExist, esFiltersExist } =
      hasFilters();

    const contactsCount =
      (params?.limit || 0) - (params.excludedLeadIds?.length || 0);

    if (
      isSelectedAll &&
      withExport &&
      !(filterParamsExist || tagsFiltersExist || esFiltersExist)
    ) {
      exportContacts(undefined, contactsCount, () => onExportSuccess());
      return;
    }

    const filterParamsWithValues = filterParams
      ?.filter((f: FilterItem) => f.value?.length)
      .map(({ esType: _esType = null as any, ...filter }) => ({
        ...filter,
      }));

    const enumeratedFilterParams = {
      ...(filterParamsWithValues?.length ? { logic: '1' } : {}),
      ...filterParamsWithValues?.reduce((acc, filter, i) => {
        acc[i + 1] = ObjectUtils.deepSnakeKeys(filter);
        return acc;
      }, {}),
    };

    const audience = await create(
      {
        ...restParams,
        filterParams: enumeratedFilterParams,
        tagsFilters,
        esFilters,
      },
      chatbotId,
    );

    if (audience) {
      notification.success({
        message: `Audience ${audience.name} has been created.`,
      });

      if (withAddToAutomation) {
        onAddToAutomation(audience);
        return;
      }

      if (withExport) {
        exportContacts(audience.id, contactsCount, () =>
          onExportSuccess(audience.name),
        );
        return;
      }

      history.push(
        BotBuilderRoutes.audiences.getLink({
          chatbotId,
          audienceId: audience.id,
        }),
      );
    }
  };

  return { createMyLeadsAudience };
};
